import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { usePermissions } from '@/hooks/usePermission';
import { useTeamMembers } from '@/features/team/hooks/useTeamMembers';
import { useTeamInvites } from '@/features/team/hooks/useTeamInvites';
import { ENTITY_TYPES, type EntityType } from '@/lib/constants/entity-types';
import { PERMISSIONS } from '@/lib/constants/permissions';
import { ROUTES } from '@/lib/constants/routes';
import { DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';
import type { ActiveMembership } from '@/lib/types/auth.types';

const S = DASHBOARD_STRINGS.TEAM;

/** Team Access page per entity type, the same pages the Workspace menu links to. */
const TEAM_ROUTE_BY_ENTITY_TYPE: Record<EntityType, string> = {
  [ENTITY_TYPES.STARTUP]: ROUTES.STARTUP_TEAM,
  [ENTITY_TYPES.INVESTOR]: ROUTES.INVESTOR_TEAM,
  [ENTITY_TYPES.TRUSTED_PARTNER]: ROUTES.PARTNER_TEAM,
};

const TEAM_PERMISSIONS = [PERMISSIONS.TEAM_MANAGE, PERMISSIONS.TEAM_INVITE] as const;

export interface DashboardTeamSnapshotProps {
  membership: ActiveMembership;
}

/**
 * Member and pending-invite counts for someone who runs the team (MYN-254).
 * Shown only with team.manage or team.invite, the same capability gate the
 * Team Access page uses for its controls; a plain member sees nothing here.
 */
export function DashboardTeamSnapshot({ membership }: DashboardTeamSnapshotProps) {
  const perms = usePermissions(TEAM_PERMISSIONS);
  const allowed = perms[PERMISSIONS.TEAM_MANAGE] || perms[PERMISSIONS.TEAM_INVITE];
  const members = useTeamMembers(allowed ? membership.entityId : undefined, membership.entityType);
  const invites = useTeamInvites(allowed ? membership.entityId : undefined);
  if (!allowed || !members.data) return null;

  const pending = (invites.data ?? []).filter((invite) => invite.status === 'pending').length;

  return (
    <Card
      shadow="soft"
      hoverEffect={false}
      className="w-full p-6!"
      title={<span className="text-teal">{S.TITLE}</span>}
      action={
        <Button asChild variant="link" className="text-sm">
          <Link to={TEAM_ROUTE_BY_ENTITY_TYPE[membership.entityType]}>{S.MANAGE}</Link>
        </Button>
      }
    >
      <p className="m-0 text-sm font-semibold text-navy">{S.MEMBERS(members.data.length)}</p>
      {pending > 0 && <p className="m-0 mt-1 text-sm text-slate">{S.PENDING(pending)}</p>}
    </Card>
  );
}
